import { useEffect, useRef, type RefObject } from 'react';

/**
 * Cierre de un popover abierto (menú, lista de opciones): un toque o clic fuera de
 * todos los `containers` lo cierra, y Escape lo cierra devolviendo el foco a `trigger`.
 * Usa `pointerdown` para que en pantallas táctiles cierre al primer toque. Un popover
 * que vive dentro de un diálogo debe atender Escape él mismo y frenar la propagación,
 * para que el diálogo no se cierre con él (ver Select).
 */
export function useDismiss(
  open: boolean,
  close: () => void,
  containers: readonly RefObject<HTMLElement | null>[],
  trigger: RefObject<HTMLElement | null>,
): void {
  // Los contenedores se leen por ref: una lista nueva en cada render no reinicia el efecto.
  const containersRef = useRef(containers);
  containersRef.current = containers;

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    const onPointer = (e: PointerEvent): void => {
      const target = e.target as Node;
      if (!containersRef.current.some((c) => c.current?.contains(target))) {
        close();
      }
    };
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        close();
        trigger.current?.focus();
      }
    };
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close, trigger]);
}
